import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useSiteSettings } from '../../context/SiteSettingsContext';

import {
  get_page_qr_choices,
  post_generate_page_qr,
  post_generate_custom_qr,
  get_generated_qr_list,
  delete_generated_qr,
  get_qr_members,
  post_assign_qr,
  post_unassign_qr,
  patch_member_designation,
} from './QRCodes.service';
import MembershipIDCard, { printMembershipCard, resolveMedia } from './MembershipIDCard';

const RAW_API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const API_BASE = RAW_API.replace(/\/api\/?$/, '');
const tabs = ['Member ID Cards', 'Website Pages', 'Custom QR'];

export default function AdminQRCodes() {
  const [activeTab, setActiveTab] = useState(0);
  const { settings: siteSettings } = useSiteSettings();

  // ─── Member ID card state ───
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [designations, setDesignations] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  // Page QR state
  const [pageChoices, setPageChoices] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [savedPageQRs, setSavedPageQRs] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [baseUrl, setBaseUrl] = useState(window.location.origin);

  // Custom QR state
  const [customUrl, setCustomUrl] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [savedCustomQRs, setSavedCustomQRs] = useState([]);
  const [customLoading, setCustomLoading] = useState(false);

  const fetchSavedQRs = useCallback(async () => {
    try {
      const res = await get_generated_qr_list();
      setSavedPageQRs(res.data.filter(q => q.qr_type === 'page'));
      setSavedCustomQRs(res.data.filter(q => q.qr_type === 'custom'));
    } catch {}
  }, []);

  const fetchMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const res = await get_qr_members();
      setMembers(res.data);
      setDesignations(
        res.data.reduce((acc, m) => ({ ...acc, [m.id]: m.designation || '' }), {})
      );
    } catch {
      toast.error('Failed to load members.');
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    get_page_qr_choices().then(res => setPageChoices(res.data)).catch(() => {});
    fetchSavedQRs();
    fetchMembers();
  }, [fetchSavedQRs, fetchMembers]);

  // Merge updated member records returned by assign/revoke/designation calls.
  const mergeMembers = (updated) => {
    const map = new Map(updated.map(m => [m.id, m]));
    setMembers(prev => prev.map(m => (map.has(m.id) ? map.get(m.id) : m)));
  };

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return members.filter(m => {
      if (statusFilter === 'assigned' && !m.qr_assigned) return false;
      if (statusFilter === 'unassigned' && m.qr_assigned) return false;
      if (!q) return true;
      return (
        (m.full_name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.membership_number || '').toLowerCase().includes(q)
      );
    });
  }, [members, memberSearch, statusFilter]);

  const visibleIds = filteredMembers.map(m => m.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? selectedIds.filter(id => !visibleIds.includes(id)) : [...new Set([...selectedIds, ...visibleIds])]);
  };

  const runAssign = async (ids) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await post_assign_qr({ member_ids: ids, base_url: window.location.origin });
      mergeMembers(res.data.members || []);
      setSelectedIds([]);
      toast.success(res.data.detail || 'Cards issued.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to issue cards.');
    } finally {
      setBusy(false);
    }
  };

  const runRevoke = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Revoke the membership ID card from ${ids.length} member(s)? They will no longer see it in their portal.`)) return;
    setBusy(true);
    try {
      const res = await post_unassign_qr({ member_ids: ids });
      mergeMembers(res.data.members || []);
      setSelectedIds([]);
      toast.success(res.data.detail || 'Cards revoked.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke cards.');
    } finally {
      setBusy(false);
    }
  };

  const saveDesignation = async (member) => {
    const value = (designations[member.id] || '').trim();
    if (value === (member.designation || '')) return; // unchanged
    setSavingId(member.id);
    try {
      const res = await patch_member_designation(member.id, { designation: value });
      mergeMembers([res.data]);
      toast.success('Designation updated.');
    } catch {
      toast.error('Failed to update designation.');
    } finally {
      setSavingId(null);
    }
  };

  const handleGeneratePageQR = async () => {
    if (!selectedPages.length) return toast.warning('Select at least one page.');
    setPageLoading(true);
    try {
      const res = await post_generate_page_qr({ pages: selectedPages, base_url: baseUrl });
      toast.success(`Generated ${res.data.count} QR code(s).`);
      await fetchSavedQRs();
      setSelectedPages([]);
    } catch {
      toast.error('Failed to generate page QR codes.');
    } finally {
      setPageLoading(false);
    }
  };

  const togglePage = (key) => {
    setSelectedPages(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectAll = () => {
    setSelectedPages(selectedPages.length === pageChoices.length ? [] : pageChoices.map(p => p.key));
  };

  const handleCustomGenerate = async () => {
    if (!customUrl.trim()) return toast.warning('Enter a URL.');
    setCustomLoading(true);
    try {
      await post_generate_custom_qr({ url: customUrl, label: customLabel });
      toast.success('QR code generated!');
      setCustomUrl('');
      setCustomLabel('');
      await fetchSavedQRs();
    } catch {
      toast.error('Failed to generate QR code.');
    } finally {
      setCustomLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) return;
    try {
      await delete_generated_qr(id);
      toast.success('QR code deleted.');
      await fetchSavedQRs();
    } catch {
      toast.error('Failed to delete QR code.');
    }
  };

  const assignedCount = members.filter(m => m.qr_assigned).length;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">QR Codes &amp; Member ID Cards</h1>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        {tabs.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
              activeTab === i
                ? 'border-[#0B5D1E] text-[#0B5D1E]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab 0: Member ID Cards */}
      {activeTab === 0 && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm border p-5">
            <p className="text-gray-600 text-sm">
              Issue a digital membership ID card to members. Once issued, the member sees their
              card (logo, photo, name, membership ID, designation and branch contact details) under
              <span className="font-semibold text-gray-800"> My ID Card</span> in their portal.
            </p>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <span className="text-gray-500">Total members: <span className="font-semibold text-gray-900">{members.length}</span></span>
              <span className="text-gray-500">Cards issued: <span className="font-semibold text-[#0B5D1E]">{assignedCount}</span></span>
            </div>
          </div>

          {/* Controls */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex flex-1 gap-3">
                <input
                  type="text"
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search by name, email or ID…"
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-[#0B5D1E] outline-none"
                />
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none"
                >
                  <option value="all">All</option>
                  <option value="assigned">Issued</option>
                  <option value="unassigned">Not issued</option>
                </select>
              </div>
            </div>

            {/* Bulk action bar */}
            {selectedIds.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
                <span className="text-sm font-medium text-[#0B5D1E]">{selectedIds.length} selected</span>
                <button
                  onClick={() => runAssign(selectedIds)}
                  disabled={busy}
                  className="bg-[#0B5D1E] text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50"
                >
                  Issue Cards
                </button>
                <button
                  onClick={() => runRevoke(selectedIds)}
                  disabled={busy}
                  className="border border-red-300 text-red-600 px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
                >
                  Revoke Cards
                </button>
                <button onClick={() => setSelectedIds([])} className="text-sm text-gray-500 hover:text-gray-700 ml-auto">
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Members table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {membersLoading ? (
              <div className="p-12 text-center text-gray-400">Loading members…</div>
            ) : filteredMembers.length === 0 ? (
              <div className="p-12 text-center text-gray-400">No members found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wide">
                    <tr>
                      <th className="px-4 py-3 text-left w-10">
                        <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} className="accent-[#0B5D1E] w-4 h-4" />
                      </th>
                      <th className="px-4 py-3 text-left">Member</th>
                      <th className="px-4 py-3 text-left">Membership ID</th>
                      <th className="px-4 py-3 text-left">Designation</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredMembers.map(m => {
                      const photo = resolveMedia(m.photo);
                      return (
                        <tr key={m.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={selectedIds.includes(m.id)} onChange={() => toggleSelect(m.id)} className="accent-[#0B5D1E] w-4 h-4" />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {photo ? (
                                <img src={photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-[#0B5D1E] text-white flex items-center justify-center text-sm font-bold">
                                  {(m.full_name || 'N').charAt(0)}
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 truncate">{m.full_name}</p>
                                <p className="text-xs text-gray-400 truncate">{m.email || '—'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{m.membership_number || '—'}</td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={designations[m.id] ?? ''}
                              onChange={e => setDesignations({ ...designations, [m.id]: e.target.value })}
                              onBlur={() => saveDesignation(m)}
                              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                              placeholder={m.membership_category_display || 'Designation'}
                              disabled={savingId === m.id}
                              className="w-40 border border-gray-200 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-[#0B5D1E] outline-none disabled:opacity-60"
                            />
                          </td>
                          <td className="px-4 py-3">
                            {m.qr_assigned ? (
                              <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-full text-xs font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Issued
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-500 border border-gray-200 px-2.5 py-1 rounded-full text-xs font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> Not issued
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => setPreview(m)}
                                className="text-xs font-semibold text-gray-500 hover:text-gray-800"
                              >
                                Preview
                              </button>
                              {m.qr_assigned ? (
                                <button
                                  onClick={() => runRevoke([m.id])}
                                  disabled={busy}
                                  className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                                >
                                  Revoke
                                </button>
                              ) : (
                                <button
                                  onClick={() => runAssign([m.id])}
                                  disabled={busy}
                                  className="bg-[#0B5D1E] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#062E10] transition disabled:opacity-50"
                                >
                                  Issue Card
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 1: Website Pages */}
      {activeTab === 1 && (
        <div className="space-y-6">
          {/* Base URL config */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Base URL <span className="text-gray-400 font-normal">(frontend origin for QR links)</span>
            </label>
            <input
              type="url"
              value={baseUrl}
              onChange={e => setBaseUrl(e.target.value)}
              className="w-full md:w-96 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-[#0B5D1E] outline-none"
              placeholder="https://yoursite.com"
            />
          </div>

          {/* Page selection */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Select Pages</h2>
              <button onClick={selectAll} className="text-sm text-[#0B5D1E] hover:underline font-medium">
                {selectedPages.length === pageChoices.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {pageChoices.map(page => (
                <label
                  key={page.key}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition ${
                    selectedPages.includes(page.key)
                      ? 'border-[#0B5D1E] bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPages.includes(page.key)}
                    onChange={() => togglePage(page.key)}
                    className="accent-[#0B5D1E] w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">{page.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-5">
              <button
                onClick={handleGeneratePageQR}
                disabled={pageLoading || !selectedPages.length}
                className="bg-[#0B5D1E] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#062E10] transition disabled:opacity-50"
              >
                {pageLoading ? 'Generating...' : `Generate QR for ${selectedPages.length} Page(s)`}
              </button>
            </div>
          </div>

          {/* Saved page QR codes */}
          {savedPageQRs.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved QR Codes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {savedPageQRs.map(item => (
                  <div key={item.id} className="bg-gray-50 rounded-xl p-5 text-center border relative">
                    <img
                      src={`${API_BASE}${item.qr_url}`}
                      alt={item.label}
                      className="w-40 h-40 mx-auto mb-3"
                    />
                    <h3 className="font-semibold text-gray-900 text-sm">{item.label}</h3>
                    <p className="text-xs text-gray-500 mt-1 break-all">{item.url}</p>
                    <div className="flex items-center justify-center gap-3 mt-3">
                      <a
                        href={`${API_BASE}${item.qr_url}`}
                        download={`QR_${item.key}.png`}
                        className="text-xs font-semibold text-[#0B5D1E] hover:underline"
                      >
                        Download
                      </a>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-xs font-semibold text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Custom QR */}
      {activeTab === 2 && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-8 max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Custom QR Code</h2>
            <p className="text-gray-600 mb-6">
              Enter any URL to generate a QR code — payment links, external sites, forms, etc.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={customLabel}
                  onChange={e => setCustomLabel(e.target.value)}
                  placeholder="e.g. Annual Dues Payment Link"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-[#0B5D1E] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">URL</label>
                <input
                  type="url"
                  value={customUrl}
                  onChange={e => setCustomUrl(e.target.value)}
                  placeholder="https://paystack.com/pay/..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-[#0B5D1E] outline-none"
                />
              </div>
              <button
                onClick={handleCustomGenerate}
                disabled={customLoading || !customUrl.trim()}
                className="bg-[#0B5D1E] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#062E10] transition disabled:opacity-50"
              >
                {customLoading ? 'Generating...' : 'Generate QR Code'}
              </button>
            </div>
          </div>

          {/* Saved custom QR codes */}
          {savedCustomQRs.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved Custom QR Codes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {savedCustomQRs.map(item => (
                  <div key={item.id} className="bg-gray-50 rounded-xl p-5 text-center border relative">
                    <img
                      src={`${API_BASE}${item.qr_url}`}
                      alt={item.label}
                      className="w-40 h-40 mx-auto mb-3"
                    />
                    <h3 className="font-semibold text-gray-900 text-sm">{item.label}</h3>
                    <p className="text-xs text-gray-500 mt-1 break-all">{item.url}</p>
                    <div className="flex items-center justify-center gap-3 mt-3">
                      <a
                        href={`${API_BASE}${item.qr_url}`}
                        download={`QR_custom_${item.id}.png`}
                        className="text-xs font-semibold text-[#0B5D1E] hover:underline"
                      >
                        Download
                      </a>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-xs font-semibold text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreview(null)}>
          <div className="bg-gray-100 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Card Preview</h3>
              <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
            </div>
            <div className="flex justify-center">
              <MembershipIDCard member={preview} logo={siteSettings?.header_logo} />
            </div>
            <div className="flex justify-center gap-3 mt-5">
              {preview.qr_assigned ? (
                <button
                  onClick={() => printMembershipCard(preview, siteSettings?.header_logo)}
                  className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition"
                >
                  Print / Save as PDF
                </button>
              ) : (
                <button
                  onClick={() => { runAssign([preview.id]); setPreview(null); }}
                  disabled={busy}
                  className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50"
                >
                  Issue This Card
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
